import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { Problem, ProblemList, ProblemStatus, UnexplainedIncident } from '../lib/types';
import { PROBLEM_STATUS_LABELS } from '../lib/types';
import { useSession } from '../lib/session';
import { useRememberedCompany } from '../lib/rememberedCompany';
import { PriorityBadge } from '../components/Badges';
import { formatDate } from '../lib/dates';

/**
 * Problems: why the same thing keeps happening.
 *
 * The page exists to stop one fault being fixed four times and understood none. Two numbers
 * carry it: how many incidents a problem accounts for — which is the argument for
 * prioritising it — and how many incidents have no cause recorded at all, which is the
 * argument for spending an afternoon on this page in the first place.
 */
export function ProblemsPage() {
  const { session } = useSession();
  const params = useParams();
  const navigate = useNavigate();

  const companies = (session?.companies ?? []).filter(
    (company) => company.capabilities['problem.manage'] !== 'none',
  );
  const { companyId: remembered, remember } = useRememberedCompany(companies);
  const companyId = params.companyId ?? remembered;

  const [data, setData] = useState<ProblemList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('open_only');
  const [open, setOpen] = useState<string | null>(null);
  const [raising, setRaising] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setData(await api.get<ProblemList>(`/companies/${companyId}/problems?status=${status}`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the problems');
    }
  }, [companyId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!params.companyId && companyId) navigate(`/companies/${companyId}/problems`, { replace: true });
  }, [params.companyId, companyId, navigate]);

  async function act(what: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await what();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  if (companies.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <h3>Problems are ours to drive</h3>
          <p>
            A problem is the cause behind a customer's incidents, and one of our own people is
            accountable for each one. Ask an administrator if you should be on this.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Service</div>
          <h1>Problems</h1>
          <p className="subtitle">
            The cause behind incidents that keep happening. Each one has somebody accountable, a
            workaround support can use today, and the delivery work that actually fixes it.
          </p>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ marginBottom: 0, minWidth: 150 }}>
            <span className="field-label">Showing</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="open_only">Still live</option>
              <option value="all">Everything</option>
              <option value="known_error">Known errors</option>
              <option value="resolved">Resolved</option>
            </select>
          </label>
          <label className="field" style={{ marginBottom: 0, minWidth: 190 }}>
            <span className="field-label">Company</span>
            <select
              value={companyId}
              onChange={(event) => {
                remember(event.target.value);
                navigate(`/companies/${event.target.value}/problems`);
              }}
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && <div className="callout mb-2">{error}</div>}

      {data && data.unexplainedCount > 0 && (
        <div className="callout mb-2">
          <strong>
            {data.unexplainedCount} incident{data.unexplainedCount === 1 ? '' : 's'} with no cause recorded.
          </strong>{' '}
          That is the pile a problem gets raised from — if the same thing is in there twice, it is
          worth a root cause.
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setRaising(true)}>
              Raise a problem from them
            </button>
          </div>
        </div>
      )}

      {raising && (
        <RaiseProblem
          companyId={companyId}
          projects={data?.projects ?? []}
          onClose={() => setRaising(false)}
          onDone={async () => {
            setRaising(false);
            await load();
          }}
        />
      )}

      {!raising && data && data.unexplainedCount === 0 && (
        <div className="row mb-2">
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setRaising(true)}>
            Raise a problem
          </button>
        </div>
      )}

      {!data ? (
        <div className="empty">Loading…</div>
      ) : data.problems.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>No problems recorded</h3>
            <p>
              Nothing matches that filter. A problem is worth raising the second time you see the
              same fault — before that it is just an incident.
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Cause</th>
                  <th>State</th>
                  <th className="right">Incidents</th>
                  <th>Owner</th>
                  <th>Workaround</th>
                  <th>Fix</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.problems.map((problem) => (
                  <ProblemRow
                    key={problem.id}
                    problem={problem}
                    expanded={open === problem.id}
                    busy={busy}
                    onToggle={() => setOpen(open === problem.id ? null : problem.id)}
                    onAct={act}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function ProblemRow({
  problem,
  expanded,
  busy,
  onToggle,
  onAct,
}: {
  problem: Problem;
  expanded: boolean;
  busy: boolean;
  onToggle: () => void;
  onAct: (what: () => Promise<unknown>) => Promise<void>;
}) {
  const [detail, setDetail] = useState<Problem | null>(null);
  const [rootCause, setRootCause] = useState('');
  const [workaround, setWorkaround] = useState('');
  const [resolution, setResolution] = useState('');

  useEffect(() => {
    if (!expanded) return;
    void api
      .get<Problem>(`/problems/${problem.id}`)
      .then((full) => {
        setDetail(full);
        setRootCause(full.rootCause ?? '');
        setWorkaround(full.workaround);
      })
      .catch(() => setDetail(null));
  }, [expanded, problem.id]);

  return (
    <>
      <tr>
        <td className="mono" style={{ fontSize: 11 }}>
          <button type="button" className="link-button" onClick={onToggle}>
            {problem.reference}
          </button>
        </td>
        <td>
          <span style={{ fontWeight: 600 }}>{problem.title}</span>
          <div style={{ marginTop: 3 }}>
            <PriorityBadge priority={problem.priority} />
          </div>
        </td>
        <td>
          <span className={`sprint-state is-${PROBLEM_TONE[problem.status]}`}>
            {PROBLEM_STATUS_LABELS[problem.status]}
          </span>
        </td>
        <td className="right mono">
          {/* The count is the argument for prioritising it, so it is not behind a click. */}
          {problem.incidentCount}
          {problem.openIncidentCount > 0 && (
            <div className="dim" style={{ fontSize: 10 }}>
              {problem.openIncidentCount} still open
            </div>
          )}
        </td>
        <td style={{ fontSize: 11 }}>
          {problem.owner ? problem.owner.name : <span className="dim">nobody yet</span>}
        </td>
        <td style={{ fontSize: 11, maxWidth: 200 }}>
          {problem.hasWorkaround ? (
            <span title={problem.workaround}>{problem.workaround.slice(0, 60)}</span>
          ) : (
            <span className="dim">none</span>
          )}
        </td>
        <td style={{ fontSize: 11 }}>
          {problem.fix ? (
            <span className={problem.fix.done ? 'badge badge-ok' : 'badge badge-accent'} title={problem.fix.title}>
              {problem.fix.key}
              {problem.fix.done ? ' done' : ''}
            </span>
          ) : (
            <span className="dim">not linked</span>
          )}
        </td>
        <td className="right" style={{ fontSize: 11 }} />
      </tr>

      {expanded && (
        <tr>
          <td colSpan={8} style={{ background: 'var(--surface-sunken)' }}>
            <div className="stack-sm" style={{ padding: '10px 4px' }}>
              {!detail ? (
                <span className="dim">Loading…</span>
              ) : (
                <>
                  <div className="row wrap" style={{ gap: 8 }}>
                    <label className="field grow" style={{ marginBottom: 0, minWidth: 260 }}>
                      <span className="field-label">Root cause — why does this happen?</span>
                      <input value={rootCause} onChange={(event) => setRootCause(event.target.value)} />
                    </label>
                    <label className="field grow" style={{ marginBottom: 0, minWidth: 260 }}>
                      <span className="field-label">Workaround — what does support say today?</span>
                      <input value={workaround} onChange={(event) => setWorkaround(event.target.value)} />
                    </label>
                  </div>

                  <div className="row wrap" style={{ gap: 7 }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={busy}
                      onClick={() => void onAct(() => api.patch(`/problems/${problem.id}`, { rootCause, workaround }))}
                    >
                      Save the write-up
                    </button>
                    {workaround.trim() && problem.status !== 'known_error' && (
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        disabled={busy}
                        title="Support has something to tell the next caller"
                        onClick={() =>
                          void onAct(() =>
                            api.patch(`/problems/${problem.id}`, { workaround, rootCause, status: 'known_error' }),
                          )
                        }
                      >
                        Mark a known error
                      </button>
                    )}
                  </div>

                  {problem.status !== 'resolved' && problem.status !== 'closed' && (
                    <div className="row wrap" style={{ gap: 7 }}>
                      <input
                        value={resolution}
                        placeholder="What finally fixed it"
                        onChange={(event) => setResolution(event.target.value)}
                        style={{ minWidth: 280 }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={busy || resolution.trim().length < 3}
                        onClick={() => void onAct(() => api.post(`/problems/${problem.id}/resolve`, { resolution }))}
                      >
                        Resolve the problem
                      </button>
                    </div>
                  )}

                  <div>
                    <div className="field-label" style={{ marginBottom: 4 }}>
                      The incidents this explains ({detail.incidents?.length ?? 0})
                    </div>
                    {(detail.incidents ?? []).length === 0 ? (
                      <span className="dim" style={{ fontSize: 12 }}>
                        None attached. A problem that explains nothing is a note, not a problem.
                      </span>
                    ) : (
                      <div className="stack-sm">
                        {(detail.incidents ?? []).map((incident) => (
                          <div key={incident.id} className="doc-row">
                            <span className="mono" style={{ fontSize: 11 }}>
                              {incident.reference}
                            </span>
                            <span className="grow">{incident.title}</span>
                            <span className="dim" style={{ fontSize: 11 }}>
                              {incident.status}
                            </span>
                            <button
                              type="button"
                              className="btn btn-quiet btn-sm"
                              disabled={busy}
                              title="This cause does not explain that one"
                              onClick={() =>
                                void onAct(() => api.del(`/problems/${problem.id}/incidents/${incident.id}`))
                              }
                            >
                              Detach
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Raising a problem from the incidents that prompted it.
 *
 * The multi-select is the point: a problem raised without naming the incidents it explains is
 * an opinion, and the count that makes it worth prioritising would be zero.
 */
function RaiseProblem({
  companyId,
  projects,
  onClose,
  onDone,
}: {
  companyId: string;
  projects: Array<{ id: string; name: string; code: string }>;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [unexplained, setUnexplained] = useState<UnexplainedIncident[] | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<UnexplainedIncident[]>(`/companies/${companyId}/unexplained-incidents`)
      .then(setUnexplained)
      .catch(() => setUnexplained([]));
  }, [companyId]);

  const toggle = (id: string) =>
    setChosen((current) => (current.includes(id) ? current.filter((row) => row !== id) : [...current, id]));

  return (
    <div className="card mb-2">
      <div className="card-head">
        <h3>Raise a problem</h3>
        <span className="dim" style={{ fontSize: 12 }}>
          {chosen.length} incident{chosen.length === 1 ? '' : 's'} selected
        </span>
      </div>
      <div className="card-pad stack-sm">
        {error && <div className="callout">{error}</div>}

        <label className="field" style={{ marginBottom: 0 }}>
          <span className="field-label">What is the underlying cause?</span>
          <input
            value={title}
            autoFocus
            placeholder="Nightly export dies on large statements"
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <label className="field" style={{ marginBottom: 0, maxWidth: 240 }}>
          <span className="field-label">Project, if known</span>
          <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            <option value="">Not known yet</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>

        <div>
          <div className="field-label" style={{ marginBottom: 4 }}>
            Which incidents does it explain?
          </div>
          {!unexplained ? (
            <span className="dim">Loading…</span>
          ) : unexplained.length === 0 ? (
            <span className="dim" style={{ fontSize: 12 }}>
              Every incident already has a cause recorded against it.
            </span>
          ) : (
            <div className="stack-sm" style={{ maxHeight: 240, overflowY: 'auto' }}>
              {unexplained.map((incident) => (
                <label key={incident.id} className="choice">
                  <input
                    type="checkbox"
                    checked={chosen.includes(incident.id)}
                    onChange={() => toggle(incident.id)}
                  />
                  <span className="mono" style={{ fontSize: 11 }}>
                    {incident.reference}
                  </span>
                  <span className="grow">{incident.title}</span>
                  <span className="dim" style={{ fontSize: 11 }}>
                    {formatDate(incident.reportedAt)}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="row" style={{ gap: 7 }}>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy || title.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.post(`/companies/${companyId}/problems`, {
                  title: title.trim(),
                  ...(projectId ? { projectId } : {}),
                  ...(chosen.length > 0 ? { incidentIds: chosen } : {}),
                });
                await onDone();
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'Could not raise it');
              } finally {
                setBusy(false);
              }
            }}
          >
            Raise it
          </button>
          <button type="button" className="btn btn-quiet btn-sm" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const PROBLEM_TONE: Record<ProblemStatus, string> = {
  identified: 'planned',
  investigating: 'active',
  known_error: 'active',
  fix_in_progress: 'active',
  resolved: 'completed',
  closed: 'completed',
};
